import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "react-feather";
import "./dropdown-input.scss";

export interface DropdownInputOption {
  label: string;
  value: string;
}

export type DropdownInputSize = "small" | "medium" | "large";

export interface DropdownInputProps {
  label?: string;
  options: DropdownInputOption[];
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  onChange?: (value: string, option: DropdownInputOption) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  hint?: string;
  size?: DropdownInputSize;
  shouldUsePortal?: boolean;
  className?: string;
  id?: string;
}

const TickIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M16.6667 5L7.5 14.1667L3.33334 10"
      stroke="#1F1F1F"
      strokeWidth="1.66667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const DropdownInput: React.FC<DropdownInputProps> = ({
  label,
  options,
  value: controlledValue,
  defaultValue,
  placeholder = "Select...",
  onChange,
  disabled = false,
  required = false,
  error,
  hint,
  size = "small",
  shouldUsePortal = false,
  className = "",
  id,
}) => {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");
  const [isOpen, setIsOpen] = useState(false);
  const [dropDirection, setDropDirection] = useState<"down" | "up">("down");
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties>({});

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const activeValue = isControlled ? controlledValue : internalValue;
  const selectedOption = options.find((o) => o.value === activeValue);
  const displayLabel = selectedOption?.label ?? placeholder;

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const gap = 4;
    const spaceBelow = window.innerHeight - triggerRect.bottom - gap;
    const estimatedMenuHeight = Math.min(options.length * 40, 240);
    const direction = spaceBelow < estimatedMenuHeight ? "up" : "down";
    setDropDirection(direction);

    if (shouldUsePortal) {
      const top =
        direction === "down"
          ? triggerRect.bottom + gap
          : triggerRect.top - gap - estimatedMenuHeight;

      setPortalStyle({
        position: "fixed",
        top,
        left: triggerRect.left,
        width: triggerRect.width,
      });
    }
  }, [shouldUsePortal, options.length]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideContainer = containerRef.current?.contains(target) ?? false;
      const insideMenu = menuRef.current?.contains(target) ?? false;

      if (!insideContainer && !insideMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    updatePosition();

    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [isOpen, updatePosition]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
  };

  const handleSelect = (option: DropdownInputOption) => {
    if (!isControlled) {
      setInternalValue(option.value);
    }
    onChange?.(option.value, option);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleTriggerKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      } else {
        const firstItem = menuRef.current?.querySelector<HTMLLIElement>(
          ".dropdown-input__option"
        );
        firstItem?.focus();
      }
    }
  };

  const handleOptionKeyDown = (
    event: React.KeyboardEvent,
    option: DropdownInputOption,
    index: number
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleSelect(option);
      return;
    }

    if (event.key === "Escape") {
      setIsOpen(false);
      triggerRef.current?.focus();
      return;
    }

    const items = menuRef.current?.querySelectorAll<HTMLLIElement>(
      ".dropdown-input__option"
    );
    if (!items) return;

    let nextIndex = -1;
    if (event.key === "ArrowDown") {
      nextIndex = (index + 1) % items.length;
    } else if (event.key === "ArrowUp") {
      nextIndex = (index - 1 + items.length) % items.length;
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = items.length - 1;
    }

    if (nextIndex >= 0) {
      event.preventDefault();
      items[nextIndex]?.focus();
    }
  };

  const renderMenu = () => (
    <ul
      ref={menuRef}
      className={`dropdown-input__menu dropdown-input__menu--${shouldUsePortal ? "portal" : dropDirection}`}
      role="listbox"
      aria-label={label}
      style={shouldUsePortal ? portalStyle : undefined}
    >
      {options.map((option, index) => (
        <li
          key={option.value}
          className={`dropdown-input__option ${option.value === activeValue ? "dropdown-input__option--selected" : ""}`}
          role="option"
          aria-selected={option.value === activeValue}
          tabIndex={0}
          onClick={() => handleSelect(option)}
          onKeyDown={(e) => handleOptionKeyDown(e, option, index)}
        >
          <span className="dropdown-input__option-label">{option.label}</span>
          {option.value === activeValue && (
            <span className="dropdown-input__check" aria-hidden="true">
              <TickIcon />
            </span>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <div
      className={`dropdown-input ${error ? "dropdown-input--error" : ""} ${className}`}
      ref={containerRef}
      id={id}
    >
      {label && (
        <label className="dropdown-input__label">
          {label}
          {required && <span className="dropdown-input__required"> *</span>}
        </label>
      )}

      <div className="dropdown-input__wrapper">
        <button
          ref={triggerRef}
          className={`dropdown-input__trigger dropdown-input__trigger--${size} ${isOpen ? "dropdown-input__trigger--open" : ""} ${!selectedOption ? "dropdown-input__trigger--placeholder" : ""} ${error ? "dropdown-input__trigger--error" : ""}`}
          type="button"
          onClick={handleToggle}
          onKeyDown={handleTriggerKeyDown}
          disabled={disabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={label}
          aria-invalid={!!error}
        >
          <span className="dropdown-input__value">{displayLabel}</span>
          <span
            className={`dropdown-input__icon ${isOpen ? "dropdown-input__icon--open" : ""}`}
            aria-hidden="true"
          >
            <ChevronDown size={16} />
          </span>
        </button>

        {isOpen && !shouldUsePortal && renderMenu()}
      </div>

      {isOpen && shouldUsePortal && createPortal(renderMenu(), document.body)}

      {hint && !error && (
        <div className="dropdown-input__hint">{hint}</div>
      )}
      {error && <div className="dropdown-input__error-text">{error}</div>}
    </div>
  );
};
